import { AXIOS_INSTANCE } from '@/lib/use-custom-instance';

/** Report posted to `POST /internal/realtime/client-errors`. */
export interface ClientErrorReport {
  kind: string;
  detail: Record<string, unknown>;
}

const MAX_BODY_BYTES = 4096;
const WINDOW_MS = 60_000;
const MAX_REPORTS_PER_WINDOW = 5;

interface Options {
  /**
   * Wire-level sender. Defaults to a thin axios POST that swallows network /
   * 404 errors (the endpoint may not exist yet — see realtime-architecture.md
   * "Observability").
   */
  send?: (body: string) => Promise<void>;
}

async function defaultSender(body: string): Promise<void> {
  try {
    await AXIOS_INSTANCE({
      url: '/internal/realtime/client-errors',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
    });
  } catch (err) {
    // 404 is expected today: the endpoint isn't built yet. Treat any failure
    // as best-effort and log at debug so we don't pollute prod consoles.
    const status =
      typeof err === 'object' && err !== null && 'response' in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined;
    if (status === 404) {
      console.debug('[realtime] client-error endpoint 404 — drop');
      return;
    }
    console.debug('[realtime] client-error send failed', err);
  }
}

/**
 * Posts adapter parse failures, Centrifugo connect errors, etc. to
 * `predictions-api`. Body is capped at 4 KB; in-memory token-bucket caps the
 * client at 5 reports/minute so a runaway loop can't flood the api.
 */
export class ClientErrorReporter {
  private readonly send: (body: string) => Promise<void>;
  private readonly timestamps: number[] = [];

  constructor(opts: Options = {}) {
    this.send = opts.send ?? defaultSender;
  }

  report(input: ClientErrorReport): void {
    if (!this.allow()) return;
    let body = JSON.stringify(input);
    if (body.length > MAX_BODY_BYTES) body = body.slice(0, MAX_BODY_BYTES);
    void this.send(body).catch((err) => {
      console.debug('[realtime] client-error reporter swallow', err);
    });
  }

  private allow(): boolean {
    const now = Date.now();
    while (this.timestamps.length > 0 && now - this.timestamps[0] >= WINDOW_MS) {
      this.timestamps.shift();
    }
    if (this.timestamps.length >= MAX_REPORTS_PER_WINDOW) return false;
    this.timestamps.push(now);
    return true;
  }
}

/** Shared singleton — most callers should import this. */
export const clientErrorReporter = new ClientErrorReporter();
